import { ref, computed } from 'vue'
import { fetchCategories, saveCategories, deleteCategory } from '../services/management'

/**
 * Composable for managing categories
 * @returns {Object} Category management state and methods
 */
export function useCategoryManagement() {
  // State
  const categories = ref([])
  const subCategories = ref({})
  const expandedCategories = ref([])
  const isLoading = ref(false)
  const isSubmitting = ref(false)
  const currentView = ref('list')
  const loadingSubCategories = ref(null)
  const searchQuery = ref('')

  /**
   * Filter categories based on search query
   */
  const filteredCategories = computed(() => {
    if (!searchQuery.value.trim()) return categories.value
    
    const query = searchQuery.value.toLowerCase()
    return categories.value.filter(category => 
      category.name.toLowerCase().includes(query)
    )
  })

  /**
   * Loads all categories
   * @param {string|number} assetId - The asset ID
   * @param {Function} i18n - Translation function
   */
  const loadCategories = async (assetId, i18n) => {
    isLoading.value = true

    const options = {
      isActive: 1,
      isCustom: true,
      includePrompts: true,
    }

    const result = await fetchCategories(assetId, options, i18n)

    categories.value = result.categories
    subCategories.value = result.subCategories

    isLoading.value = false
    return result
  }

  /**
   * Clears all category data
   */
  const clearCategories = () => {
    categories.value = []
    subCategories.value = {}
    expandedCategories.value = []
    currentView.value = 'list'
  }

  /**
   * Fetches subcategories for a parent category
   * @param {Object} options - Options with parentId
   */
  const fetchSubCategories = async ({ parentId }) => {
    if (subCategories.value[parentId]) return

    subCategories.value[parentId] = []

    const parentCategory = categories.value.find(c => c.id === parentId)

    if (parentCategory?.children?.length) {
      subCategories.value[parentId] = parentCategory.children
    }
  }

  /**
   * Toggles category expansion
   * @param {Object} data - Category data with id and level info
   */
  const toggleCategory = async ({ id, isLevel1 }) => {
    if (isLevel1) {
      processLevel1Categories(id)
    }

    await toggleCategoryExpansion(id)
  }

  /**
   * Process level 1 categories
   * @param {string|number} currentCategoryId - The current category ID
   */
  const processLevel1Categories = (currentCategoryId) => {
    // Find other expanded level 1 categories
    const otherLevel1Expanded = expandedCategories.value.filter(categoryId => {
      const category = categories.value.find(c => c.id === categoryId)
      return category?.level === 1 && categoryId !== currentCategoryId
    })

    // Clear subcategories for other expanded level 1 categories
    otherLevel1Expanded.forEach(categoryId => {
      delete subCategories.value[categoryId]
    })

    // Filter expanded categories
    expandedCategories.value = expandedCategories.value.filter(categoryId => {
      const category = categories.value.find(c => c.id === categoryId)
      return category?.level !== 1 || categoryId === currentCategoryId
    })
  }

  /**
   * Toggle category expansion
   * @param {string|number} categoryId - The category ID
   */
  const toggleCategoryExpansion = async (categoryId) => {
    const index = expandedCategories.value.indexOf(categoryId)

    if (index === -1) {
      expandedCategories.value.push(categoryId)
      await fetchSubCategories({ parentId: categoryId })
    } else {
      expandedCategories.value.splice(index, 1)
    }
  }

  /**
   * Handle category deletion
   * @param {Object} categoryInfo - Category information
   * @param {string|number} assetId - The asset ID
   * @param {Function} i18n - Translation function
   */
  const handleCategoryDeleted = async (categoryInfo, assetId, i18n) => {
    delete subCategories.value[categoryInfo.id]

    if (categoryInfo.parentId) {
      delete subCategories.value[categoryInfo.parentId]

      if (expandedCategories.value.includes(categoryInfo.parentId)) {
        await fetchSubCategories({
          parentId: categoryInfo.parentId,
        })
      }
    }

    expandedCategories.value = expandedCategories.value.filter(
      id => id !== categoryInfo.id,
    )

    await loadCategories(assetId, i18n)
  }

  /**
   * Save categories
   * @param {Object} data - Category data to save
   * @param {string|number} assetId - The asset ID
   * @param {Function} i18n - Translation function
   */
  const handleSaveCategories = async (data, assetId, i18n) => {
    isSubmitting.value = true

    const result = await saveCategories(
      assetId,
      data.categories,
      i18n,
    )

    if (result.success) {
      currentView.value = 'list'
      await loadCategories(assetId, i18n)
    }

    isSubmitting.value = false
    return result
  }

  /**
   * Delete a category
   * @param {Object} category - The category to delete
   * @param {string|number} assetId - The asset ID
   * @param {Function} i18n - Translation function
   */
  const deleteCategoryAction = async (category, assetId, i18n) => {
    const result = await deleteCategory(category, assetId, i18n)
    
    if (result.success) {
      await handleCategoryDeleted(result.deletedInfo, assetId, i18n)
    }
    
    return result
  }

  /**
   * Switch views between list and create
   * @param {string} view - The view to switch to
   */
  const switchView = (view) => {
    currentView.value = view
  }

  /**
   * Sets search query
   * @param {string} query - The search query
   */
  const setSearchQuery = (query) => {
    searchQuery.value = query
  }

  return {
    // State
    categories,
    subCategories,
    expandedCategories,
    isLoading,
    isSubmitting,
    currentView,
    loadingSubCategories,
    searchQuery,
    
    // Computed
    filteredCategories,
    
    // Methods
    loadCategories,
    clearCategories,
    toggleCategory,
    handleCategoryDeleted,
    handleSaveCategories,
    deleteCategory: deleteCategoryAction,
    switchView,
    setSearchQuery,
    fetchSubCategories,
  }
}
